<template>
    <flex-container :gap="Size.lg">
        <text-decorator :size="FontSize.xl" bold>
            Systeem instellingen
        </text-decorator>

        <card-normal without-padding>
            <div class="temp-settings">
                <flex-container
                    :gap="null"
                    class="temp-settings__menu"
                    direction="column"
                >
                    <a
                        v-for="route in generalSettingRoutes.filter((filter) => filter.enabled)"
                        :key="route.url"
                        :href="route.url"
                        :class="{
                            'temp-settings__url': true,
                            'temp-settings__url--active': $route.matched.some(({ name }) => name === route.name ?? null),
                        }"
                    >
                        {{ route.label }}
                    </a>

                    <span class="temp-settings__menu-title">
                        Virtuele Collega
                    </span>

                    <a
                        v-for="route in virtualColleagueSettings.filter((filter) => filter.enabled)"
                        :key="route.url"
                        :href="route.url"
                        :class="{
                            'temp-settings__url': true,
                            'temp-settings__url--active': $route.matched.some(({ name }) => name === route.name ?? null),
                        }"
                    >
                        {{ route.label }}
                    </a>

                    <span class="temp-settings__menu-title">
                        Smart Marketing
                    </span>

                    <a
                        v-for="route in smartMarketingSettings.filter((filter) => filter.enabled)"
                        :key="route.url"
                        :href="route.url"
                        :class="{
                            'temp-settings__url': true,
                            'temp-settings__url--active': $route.matched.some(({ name }) => name === route.name ?? null),
                        }"
                    >
                        {{ route.label }}
                    </a>

                    <span class="temp-settings__menu-title">
                        Bezichtigingsportaal
                    </span>

                    <a
                        v-for="route in customerPortalSettings.filter((filter) => filter.enabled)"
                        :key="route.url"
                        :href="route.url"
                        :class="{
                            'temp-settings__url': true,
                            'temp-settings__url--active': $route.matched.some(({ name }) => name === route.name ?? null),
                        }"
                    >
                        {{ route.label }}
                    </a>
                </flex-container>
                <flex-container>
                    <router-view />
                </flex-container>
            </div>
        </card-normal>
    </flex-container>
</template>

<script setup lang="ts">
    import { FlexContainer, FontSize, Size } from '@keyvue/one-ui';
    import createLegacyUrl from '@/utils/legacyUrls';
    import useClient from '@composables/useClient';
    import { CredentialsType, TypeformCredentials, WidgetFlowType } from '@keyvue/one-api-client-legacy';
    import { computed } from 'vue';

    const { currentLegacyCompany } = useClient();

    const informationFlowActive = computed(() => (
        currentLegacyCompany.value.activatedWidgetFlowTypes.includes(WidgetFlowType.INFORMATION)
    ));

    const checklistFlowActive = computed(() => (
        currentLegacyCompany.value.activatedWidgetFlowTypes.includes(WidgetFlowType.CHECKLIST)
    ));

    const typeformEnabled = computed(() => {
        const credentials = currentLegacyCompany.value.getCredentialsWithType(CredentialsType.TYPEFORM) as TypeformCredentials[];
        if (credentials.length === 0) {
            return false;
        }

        return credentials[0].workspaceId !== null;
    });

    const generalSettingRoutes: Record<string, any>[] = [
        {
            label: 'Algemeen',
            url: createLegacyUrl('company/settings/general'),
            enabled: true,
        },
        {
            label: 'Huisstijl',
            url: createLegacyUrl('company/settings/branding'),
            enabled: true,
        },
        {
            label: 'Pijplijn',
            url: createLegacyUrl('company/settings/pipelines'),
            enabled: true,
        },
        {
            label: 'Fase en redenen',
            url: createLegacyUrl('company/settings/statuses'),
            enabled: true,
        },
        {
            label: 'Lead types',
            url: createLegacyUrl('company/settings/sources'),
            enabled: !currentLegacyCompany.value.pipelinesEnabled,
        },
        {
            label: 'Projecten',
            url: createLegacyUrl('projects'),
            enabled: true,
        },
        {
            label: 'Rapportages',
            url: createLegacyUrl('company/settings/reports'),
            enabled: true,
        },
        {
            label: 'Gebruikers',
            url: createLegacyUrl('company/settings/users'),
            enabled: true,
        },
        {
            label: 'Teams',
            url: createLegacyUrl('company/settings/teams'),
            enabled: true,
        },
        {
            label: 'Realworks Tokens',
            url: createLegacyUrl('company/settings/realworks_credentials'),
            enabled: true,
        },
        {
            label: 'Opdrachten',
            url: createLegacyUrl('company/settings/orders'),
            enabled: true,
        },
        {
            label: 'Omzet verdeling',
            url: createLegacyUrl('company/settings/shares'),
            enabled: true,
        },
        {
            label: 'Docflow',
            url: createLegacyUrl('company/settings/documents'),
            enabled: true,
        },
        {
            label: 'Gamification',
            url: createLegacyUrl('company/settings/gamification'),
            enabled: true,
        },
    ];

    const virtualColleagueSettings: Record<string, any>[] = [
        {
            label: 'Algemeen',
            url: createLegacyUrl('company/settings/virtual-colleague/general'),
            enabled: true,
        },
        {
            label: 'Leads aanmaken',
            url: createLegacyUrl('company/settings/virtual-colleague/triggered-referrals'),
            enabled: true,
        },
        {
            label: 'Fase & status bijhouden',
            url: createLegacyUrl('company/settings/virtual-colleague/realworks_status_rules'),
            enabled: true,
        },
        {
            label: 'Actie ondernemen',
            url: createLegacyUrl('company/settings/virtual-colleague/triggered-actions'),
            enabled: true,
        },
        {
            label: 'Leads sluiten',
            url: createLegacyUrl('company/settings/virtual-colleague/auto_close'),
            enabled: true,
        },
        {
            label: 'Omzet verwachting',
            url: createLegacyUrl('company/settings/virtual-colleague/turnover'),
            enabled: true,
        },
        {
            label: 'A.I.',
            url: '/settings/virtual-colleague/ai',
            enabled: true,
            name: 'settings-virtual-colleague-ai',
        },
    ];

    const smartMarketingSettings: Record<string, any>[] = [
        {
            label: 'Algemeen',
            url: createLegacyUrl('company/settings/smart_marketing'),
            enabled: true,
        },
        {
            label: 'Emma Avatar',
            url: createLegacyUrl('company/settings/emma_avatar'),
            enabled: true,
        },
        {
            label: 'Emma Widget',
            url: createLegacyUrl('company/settings/emma_widget'),
            enabled: true,
        },
        {
            label: 'Emma Chat',
            url: createLegacyUrl('company/settings/emma_chat'),
            enabled: true,
        },
        {
            label: 'Emma Urls',
            url: createLegacyUrl('company/settings/emma_urls'),
            enabled: true,
        },
        {
            label: 'Emma Waardebepaling',
            url: createLegacyUrl('company/settings/emma_valuation'),
            enabled: true,
        },
        {
            label: 'Emma Information Formulier',
            url: createLegacyUrl('company/settings/emma_information'),
            enabled: informationFlowActive.value,
        },
        {
            label: 'Emma Checklist',
            url: createLegacyUrl('company/settings/checklist'),
            enabled: checklistFlowActive.value,
        },
        {
            label: 'Emma Directmail',
            url: createLegacyUrl('company/settings/emma_direct_mail'),
            enabled: true,
        },
    ];

    const customerPortalSettings: Record<string, any>[] = [
        {
            label: 'Algemeen',
            url: createLegacyUrl('company/settings/typeform/general'),
            enabled: true,
        },
        {
            label: 'Portaal huisstijl',
            url: createLegacyUrl('company/settings/typeform/customerportal'),
            enabled: true,
        },
        {
            label: 'Enquête huisstijl',
            url: createLegacyUrl('company/settings/typeform/theme'),
            enabled: true,
        },
        {
            label: 'Enquêtes',
            url: createLegacyUrl('company/settings/typeform/forms'),
            enabled: typeformEnabled.value,
        },
    ];
</script>

<style lang="scss" scoped>
.temp-settings {
    display: grid;
    grid-template-columns: 250px 1fr;

    &__menu {
        border-right: 1px solid color(gray-200);
        padding-top: size(xl);
        padding-bottom: size(md);
    }

    &__url {
        display: flex;
        align-items: center;
        text-decoration: none;
        font-size: font-size(sm);
        padding-left: size(xl);
        height: size(xl);
        color: color(gray-800);

        &--active {
            border-left: 2px solid color(brand-green);
            color: color(brand-green);
        }
    }

    &__menu-title {
        display: flex;
        position: relative;
        align-items: center;
        text-decoration: none;
        font-size: font-size(xs);
        padding-left: size(xl);
        height: size(xl);
        color: color(brand-green);
        font-weight: 500;
        width: 100%;
        box-sizing: border-box;
        margin-top: size(xl);

        &::after {
            position: absolute;
            content: '';
            bottom: 8px;
            left: 32px;
            right: 33px;
            height: 1px;
            background-color: color(brand-green);
        }
    }
}
</style>
